import { useCallback, useEffect, useRef, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { createANNLLMAnalyzer, type LLMAnalysisResultItem, type LLMMetadata, type PaataleberettigetResolution } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { findAllServitutDocumentAnnotations, isServitutPdfDocumentAnnotation, mapAktPagesToCutSelection, normalizePages } from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { analysisFieldLabel, isServitutAnalysisFieldNote, listFieldNotesOfType, type ServitutAnalysisFieldKey } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { cutPdfToSelectedPages, getPdfPageCount } from "src/modules/ANNModule/ANNPDFCutter";
import { uploadOverlayImage } from "src/modules/ANNModule/ANNImageOverlayStorage";
import { pdfHasExtractableText } from "src/modules/ANNModule/pdfHasExtractableText";
import { AnnotationContextType, type ANNAnnotationRecord, type SaveAnnotationInput } from "src/modules/ANNModule/ANNTypes";
import { annLoadAnnotationsSuccess, annSaveAnnotationSuccess, type ANNDispatch } from "src/modules/ANNModule/ANNState";
import { message } from "src/components/Message";
import type { SFEColorEntry } from "src/components/ServitutRapportData";
import { hydrateRowsFromAnn, listAnnotationsForRow } from "../adapters/annHydrate";
import { buildIgnoreNoteSaveInput, buildPageDocumentSaveInput, canCommitPagesWithoutPdfFetch, commitFieldNote } from "../adapters/annCommit";
import { findIgnoreNotes, isServitutIgnoreNote } from "../model/ignore";
import { contentResultFromItem, locateResultsFromAnalysis, stampContentCaseDescription, withSkippedPaataleResolution } from "../adapters/llm";
import { bindRowToAkt, bindRowToLandRegistry, fetchDigitalXmlMetadata, prepareServitutReport, type PreparedAktFile } from "../adapters/landRegistryPrepare";
import { parseTingbogAktIdInput } from "src/managers/TingbogDownload";
import { buildServitutExcelBlob, buildServitutZip } from "../adapters/export";
import { ExportData } from "src/managers/ExportData";
import {
   SERVITUT_ANALYSIS_CONCURRENCY,
   SERVITUT_CONTENT_CONCURRENCY,
   SERVITUT_NOTE_SAVE_CONCURRENCY,
   SERVITUT_PDF_SAVE_CONCURRENCY,
   createAbortError,
   groupBy,
   isAbortError,
   runWithConcurrency,
} from "../adapters/jobs";
import {
   buildRowMetadata,
   documentFilename,
   getCachedRowPdfBlob,
   getCutPdfForContent,
   getRowSourcePdfBlob,
   locateUploadedRow,
   usesLocalPdfForLocate,
} from "../adapters/rowIo";
import { canonicalDocumentId } from "../model/identity";
import { isDigitalRow, isUserFileDocument, needsDeferredAktFetch, needsLandRegistryRebind, type PreparedAktSource, type ServitutRow } from "../model/row";
import {
   selectCanLocate,
   selectCanResolvePaatale,
   selectContentAnalysisMode,
   selectDisplayedFieldText,
   selectHasContentAnalyzableRows,
   selectHasLocatableRows,
   selectHistoricalPaataleName,
   selectIsApproved,
   selectIsRejected,
   selectOrderedRows,
   selectPageApprovalPlan,
} from "./selectors";
import { ServitutReportActionType } from "./actions";
import type { ServitutReportDispatch } from "./context";
import type { ServitutReportState } from "../model/report";

export type ServitutSessionUser = {
   userId?: string;
   userName?: string;
   organizationId?: string;
};

export type ServitutAnnIo = {
   saveAnnotation: (input: SaveAnnotationInput) => Promise<ANNAnnotationRecord>;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
   annDispatch: ANNDispatch;
};

function requireSession(session: ServitutSessionUser): session is Required<ServitutSessionUser> {
   if (!session.userId || !session.userName || !session.organizationId) {
      message.error(Localization.getText("No organization selected"));
      return false;
   }
   return true;
}

function looksLikeXml(value?: string | null): boolean {
   const text = String(value || "").trim();
   return text.startsWith("<") || /<\?xml/i.test(text);
}

function upsertAnnRecords(annDispatch: ANNDispatch, records: ANNAnnotationRecord[]) {
   records.forEach((record) => {
      if (record.id) {
         annDispatch(annSaveAnnotationSuccess(record));
      }
   });
}

function failedPaatale(historicalName: string, status: "not_found" | "error"): PaataleberettigetResolution {
   return {
      historical_name: historicalName,
      suggested_current_name: null,
      status,
      successor_chain: [],
      reasoning: Localization.getText("Current entitled party AI could not find"),
      alternatives: [],
      sources: [],
      searched_at: new Date().toISOString(),
      model: "",
      disclaimer: "",
   };
}

async function fetchAndBindLandRegistry(
   dispatch: ServitutReportDispatch,
   getState: () => ServitutReportState,
   rowId: string,
   options?: {
      filesByDocument?: Map<string, PreparedAktFile>;
      inflightByDocument?: Map<string, Promise<PreparedAktFile>>;
      signal?: AbortSignal;
   },
) {
   const row = getState().rows[rowId];
   if (!row || (row.job !== "idle" && row.job !== "saving" && row.job !== "fetching")) {
      return;
   }
   if (options?.signal?.aborted) {
      return;
   }
   if (row.job === "idle" || row.job === "saving") {
      dispatch({ type: ServitutReportActionType.FetchAktStarted, payload: { rowIds: [rowId] } });
   }
   try {
      const latest = getState().rows[rowId] || row;
      const hint = latest.landRegistry.includedBfes
         .map((bfe) => getState().aktSearchByBfe[String(bfe)])
         .find(Boolean);
      const bound = await bindRowToLandRegistry(latest, {
         stamOplysninger: hint,
         filesByDocument: options?.filesByDocument,
         inflightByDocument: options?.inflightByDocument,
         signal: options?.signal,
      });
      if (options?.signal?.aborted || !getState().rows[rowId]) {
         return;
      }
      dispatch({
         type: ServitutReportActionType.BindLandRegistry,
         payload: {
            rowId,
            document: bound.row.document,
            pdfStatus: bound.row.pdfStatus,
            sourceMessage: bound.row.sourceMessage,
            metadata: bound.metadata,
         },
      });
   } catch (error) {
      if (options?.signal?.aborted || isAbortError(error)) {
         return;
      }
      console.error("[Servitutredegørelse] failed to bind land-registry akt", rowId, error);
      dispatch({ type: ServitutReportActionType.FetchAktFailed, payload: { rowIds: [rowId] } });
   }
}

function canBindPendingAkt(row: ServitutRow | undefined): row is ServitutRow {
   return Boolean(row && needsDeferredAktFetch(row) && (row.job === "idle" || row.job === "fetching"));
}

async function bindPendingAktRows(
   dispatch: ServitutReportDispatch,
   getState: () => ServitutReportState,
   signal?: AbortSignal,
) {
   const pending = selectOrderedRows(getState()).filter(canBindPendingAkt);
   if (!pending.length) {
      return;
   }
   dispatch({ type: ServitutReportActionType.AktFetchBatchStarted, payload: { rowIds: pending.map((row) => row.id) } });
   const filesByDocument = new Map<string, PreparedAktFile>();
   const inflightByDocument = new Map<string, Promise<PreparedAktFile>>();
   await Promise.all(pending.map(async (row) => {
      if (signal?.aborted) {
         return;
      }
      const latest = getState().rows[row.id];
      if (!canBindPendingAkt(latest)) {
         return;
      }
      await fetchAndBindLandRegistry(dispatch, getState, row.id, { filesByDocument, inflightByDocument, signal });
   }));
}

function replaceAnnScopes(annDispatch: ANNDispatch, records: ANNAnnotationRecord[], organizationId?: string) {
   const byDocument = new Map<string, ANNAnnotationRecord[]>();
   records.forEach((record) => {
      const documentId = String(record.documentId || "");
      if (!documentId) {
         return;
      }
      const list = byDocument.get(documentId) ?? [];
      list.push(record);
      byDocument.set(documentId, list);
   });
   byDocument.forEach((items, documentId) => {
      annDispatch(annLoadAnnotationsSuccess(AnnotationContextType.Servitut, documentId, organizationId, items));
   });
}

export function useServitutReportEffects(
   dispatch: ServitutReportDispatch,
   getState: () => ServitutReportState,
   session: ServitutSessionUser,
   ann: ServitutAnnIo,
) {
   const locateAbortsRef = useRef(new Set<AbortController>());
   const contentAbortsRef = useRef(new Set<AbortController>());
   const aktAbortsRef = useRef(new Set<AbortController>());
   const fieldSaveRef = useRef(new Set<string>());
   const [aiProgress, setAiProgress] = useState<{ completed: number; total: number } | null>(null);

   function abortBucket(kind: "locate" | "content" | "akt") {
      return kind === "locate" ? locateAbortsRef : kind === "content" ? contentAbortsRef : aktAbortsRef;
   }

   function trackAbort(kind: "locate" | "content" | "akt"): AbortController {
      const controller = new AbortController();
      const bucket = abortBucket(kind);
      bucket.current.add(controller);
      controller.signal.addEventListener("abort", () => bucket.current.delete(controller), { once: true });
      return controller;
   }

   function abortJobs(kind: "locate" | "content" | "akt") {
      const bucket = abortBucket(kind);
      [...bucket.current].forEach((controller) => controller.abort());
      bucket.current.clear();
   }

   useEffect(() => () => {
      abortJobs("locate");
      abortJobs("content");
      abortJobs("akt");
   }, []);

   const publishAnn = useCallback((records: ANNAnnotationRecord[]) => {
      upsertAnnRecords(ann.annDispatch, records);
      dispatch({ type: ServitutReportActionType.AnnRecordsChanged, payload: { records } });
   }, [ann.annDispatch, dispatch]);

   const prepare = useCallback(async (colorList: SFEColorEntry[], datoLoebenrFilter?: string) => {
      dispatch({
         type: ServitutReportActionType.LoadStarted,
         payload: { stage: "registry", completed: 0, total: Math.max(colorList.length, 1) },
      });
      try {
         const prepared = await prepareServitutReport(colorList, {
            organizationId: session.organizationId,
            datoLoebenrFilter,
            onProgress: (progress) => dispatch({ type: ServitutReportActionType.LoadProgress, payload: progress }),
         });
         const hydrated = await hydrateRowsFromAnn(
            prepared.rows,
            session.organizationId,
            { hasCaseDescription: Boolean((prepared.caseDescription || getState().caseDescription).trim()) },
         );
         dispatch({ type: ServitutReportActionType.ReportPrepared, payload: { ...prepared, rows: hydrated.rows } });
         replaceAnnScopes(ann.annDispatch, hydrated.records, session.organizationId);
         void bindPendingAktRows(dispatch, getState, trackAbort("akt").signal);
      } catch (error) {
         const details = error instanceof Error ? error.message : "";
         const text = details
            ? Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: details })
            : Localization.getText("An error occurred while generating the report. Please try again later or contact support.");
         dispatch({ type: ServitutReportActionType.LoadFailed, payload: text });
         message.error(text);
      }
   }, [ann.annDispatch, dispatch, getState, session.organizationId]);

   const hydrateFromAnn = useCallback(async (options?: { bindPendingAkts?: boolean }) => {
      const state = getState();
      const rows = selectOrderedRows(state);
      if (!rows.length) {
         return;
      }
      const hydrated = await hydrateRowsFromAnn(rows, session.organizationId, {
         hasCaseDescription: Boolean(state.caseDescription.trim()),
      });
      dispatch({ type: ServitutReportActionType.AnnHydrated, payload: { rows: hydrated.rows } });
      replaceAnnScopes(ann.annDispatch, hydrated.records, session.organizationId);
      if (options?.bindPendingAkts) {
         void bindPendingAktRows(dispatch, getState, trackAbort("akt").signal);
      }
   }, [ann.annDispatch, dispatch, getState, session.organizationId]);

   const locateRows = useCallback(async (rowIds?: string[]) => {
      if (!requireSession(session)) {
         return;
      }
      const state = getState();
      const candidates = selectOrderedRows(state).filter((row) => (
         selectCanLocate(row)
         && row.job === "idle"
         && (!rowIds || rowIds.includes(row.id))
      ));
      if (!candidates.length) {
         if (!rowIds) {
            message.error(Localization.getText("There are no documents to locate in this view."));
         }
         return;
      }
      const ids = candidates.map((row) => row.id);
      dispatch({ type: ServitutReportActionType.LocateStarted, payload: { rowIds: ids } });
      const abort = trackAbort("locate");
      setAiProgress((prev) => ({ completed: prev?.completed ?? 0, total: (prev?.total ?? 0) + ids.length }));
      const analyzer = createANNLLMAnalyzer({
         userRef: session.userId,
         userName: session.userName,
         organizationId: session.organizationId,
      });
      let failed = 0;
      const finish = (doneIds: string[], ok: boolean) => {
         if (!ok) {
            dispatch({ type: ServitutReportActionType.LocateFailed, payload: { rowIds: doneIds } });
         }
         setAiProgress((prev) => prev ? { ...prev, completed: Math.min(prev.total, prev.completed + doneIds.length) } : prev);
      };
      try {
         const remote = candidates.filter((row) => !usesLocalPdfForLocate(row) && row.document.kind === "tingbog_akt");
         const metadata = remote.map((row) => withSkippedPaataleResolution(
            buildRowMetadata(row, state.metadataById[row.id], state.caseDescription),
         ));
         const batches = groupBy(metadata, (entry) => entry.documentUrl || entry.id || entry.aktId || entry.document);
         await runWithConcurrency(batches, SERVITUT_ANALYSIS_CONCURRENCY, async (batch) => {
            if (abort.signal.aborted) {
               return;
            }
            const batchIds = batch.map((entry) => entry.id).filter((id): id is string => Boolean(id));
            try {
               const result = await analyzer.locateServitutBatch(batch, abort.signal);
               if (abort.signal.aborted) {
                  return;
               }
               dispatch({
                  type: ServitutReportActionType.LocateSucceeded,
                  payload: { results: locateResultsFromAnalysis(selectOrderedRows(getState()), result, new Set(batchIds)) },
               });
               finish(batchIds, true);
            } catch (error) {
               if (isAbortError(error)) {
                  return;
               }
               failed += 1;
               finish(batchIds, false);
            }
         }, abort.signal);

         const local = candidates.filter(usesLocalPdfForLocate);
         await runWithConcurrency(local, SERVITUT_CONTENT_CONCURRENCY, async (row) => {
            if (abort.signal.aborted) {
               return;
            }
            try {
               const latest = getState().rows[row.id] || row;
               const item = await locateUploadedRow(
                  latest,
                  buildRowMetadata(latest, getState().metadataById[latest.id], getState().caseDescription),
                  analyzer,
                  abort.signal,
               );
               dispatch({ type: ServitutReportActionType.LocateSucceeded, payload: { results: [{ rowId: latest.id, item }] } });
               finish([row.id], true);
            } catch (error) {
               if (isAbortError(error) || abort.signal.aborted) {
                  return;
               }
               failed += 1;
               finish([row.id], false);
            }
         }, abort.signal);

         if (abort.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            dispatch({ type: ServitutReportActionType.LocateFailed, payload: { rowIds: ids.filter((id) => getState().rows[id]?.job === "locating") } });
            return;
         }
         if (failed) {
            message.error(Localization.getFormattedText("{count} akt batches could not be analyzed.", { count: failed }));
         } else if (rowIds?.length === 1) {
            message.info(Localization.getText("AI page finding completed."));
         }
      } catch (error) {
         if (isAbortError(error) || abort.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
         dispatch({ type: ServitutReportActionType.LocateFailed, payload: { rowIds: ids } });
      } finally {
         locateAbortsRef.current.delete(abort);
         if (locateAbortsRef.current.size === 0) {
            setAiProgress(null);
         }
      }
   }, [dispatch, getState, session]);

   const analyzeContentRows = useCallback(async (rowIds?: string[]) => {
      if (!requireSession(session)) {
         return;
      }
      const state = getState();
      const candidates = selectOrderedRows(state).filter((row) => (
         selectIsApproved(row)
         && row.job === "idle"
         && (!rowIds || rowIds.includes(row.id))
      ));
      if (!candidates.length) {
         if (!rowIds) {
            message.error(Localization.getText("There are no approved easements to analyze in this view."));
         }
         return;
      }
      const ids = candidates.map((row) => row.id);
      dispatch({ type: ServitutReportActionType.ContentStarted, payload: { rowIds: ids } });
      const abort = trackAbort("content");
      setAiProgress((prev) => ({ completed: prev?.completed ?? 0, total: (prev?.total ?? 0) + ids.length }));
      const analyzer = createANNLLMAnalyzer({
         userRef: session.userId,
         userName: session.userName,
         organizationId: session.organizationId,
      });
      let failed = 0;
      try {
         await runWithConcurrency(ids, SERVITUT_CONTENT_CONCURRENCY, async (rowId) => {
            if (abort.signal.aborted) {
               return;
            }
            try {
               const latest = getState().rows[rowId];
               if (!latest || !selectIsApproved(latest)) {
                  return;
               }
               const usedCase = getState().caseDescription.trim() || null;
               let metadata = withSkippedPaataleResolution({
                  ...buildRowMetadata(latest, getState().metadataById[rowId], usedCase || undefined),
                  caseDescription: usedCase,
               });
               if (isDigitalRow(latest) && !isUserFileDocument(latest) && !looksLikeXml(metadata.document)) {
                  const fresh = await fetchDigitalXmlMetadata(latest);
                  if (fresh) {
                     metadata = withSkippedPaataleResolution({ ...fresh, caseDescription: usedCase });
                     dispatch({
                        type: ServitutReportActionType.BindLandRegistry,
                        payload: {
                           rowId,
                           document: latest.document,
                           pdfStatus: latest.pdfStatus,
                           sourceMessage: latest.sourceMessage,
                           metadata: fresh,
                        },
                     });
                  }
               }
               const mode = selectContentAnalysisMode(latest, metadata);
               if (mode === "none") {
                  return;
               }
               let item: LLMAnalysisResultItem | undefined;
               if (mode === "xml") {
                  const result = stampContentCaseDescription(await analyzer.analyzePdfUrlBatch([metadata], abort.signal), usedCase);
                  item = result.items?.find((entry) => entry.id === rowId) || result.items?.[0];
               } else {
                  const cutPdf = await getCutPdfForContent(latest, abort.signal) || await getRowSourcePdfBlob(latest, abort.signal);
                  if (!cutPdf) {
                     throw new Error("No cut PDF available");
                  }
                  item = await analyzer.evaluateServitutDocument({
                     metadata,
                     pdfBlob: cutPdf,
                     documentFileName: documentFilename(latest),
                     signal: abort.signal,
                  });
               }
               if (!item || abort.signal.aborted) {
                  return;
               }
               dispatch({
                  type: ServitutReportActionType.ContentSucceeded,
                  payload: { results: [contentResultFromItem(getState().rows[rowId] || latest, { ...item, id: rowId }, usedCase)] },
               });
               const analyzed = getState().rows[rowId];
               if (analyzed?.analysis && selectIsApproved(analyzed) && session.organizationId) {
                  const listed = await listAnnotationsForRow(analyzed, session.organizationId);
                  const existing = findAllServitutDocumentAnnotations(listed, analyzed.identity.datoloebenr)[0];
                  if (existing?.document?.s3Key && session.userId && session.userName) {
                     const record = await ann.saveAnnotation(buildPageDocumentSaveInput(analyzed, {
                        organizationId: session.organizationId,
                        userId: session.userId,
                        userName: session.userName,
                     }, {
                        s3Key: existing.document.s3Key,
                        filename: existing.document.filename || documentFilename(analyzed),
                        s3Url: existing.document.s3Url,
                        mimeType: existing.document.mimeType,
                        size: existing.document.size,
                        existing,
                     }));
                     upsertAnnRecords(ann.annDispatch, [record]);
                  }
               }
            } catch (error) {
               if (isAbortError(error) || abort.signal.aborted) {
                  return;
               }
               failed += 1;
               dispatch({ type: ServitutReportActionType.ContentFailed, payload: { rowIds: [rowId] } });
            } finally {
               setAiProgress((prev) => prev ? { ...prev, completed: Math.min(prev.total, prev.completed + 1) } : prev);
            }
         }, abort.signal);
         if (abort.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            dispatch({
               type: ServitutReportActionType.ContentFailed,
               payload: { rowIds: ids.filter((id) => getState().rows[id]?.job === "analyzing") },
            });
            return;
         }
         if (failed) {
            message.error(Localization.getFormattedText("{count} easements could not be analyzed.", { count: failed }));
         } else if (rowIds?.length === 1) {
            message.info(Localization.getText("AI analysis completed."));
         }
      } catch (error) {
         if (isAbortError(error) || abort.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
         dispatch({ type: ServitutReportActionType.ContentFailed, payload: { rowIds: ids } });
      } finally {
         contentAbortsRef.current.delete(abort);
         if (contentAbortsRef.current.size === 0) {
            setAiProgress(null);
         }
      }
   }, [ann, dispatch, getState, session]);

   const finishRowCommit = useCallback((rowId: string, records: ANNAnnotationRecord[]) => {
      dispatch({ type: ServitutReportActionType.CommitSucceeded, payload: { records, rowIds: [rowId] } });
      upsertAnnRecords(ann.annDispatch, records);
      dispatch({ type: ServitutReportActionType.MarkSaved, payload: { rowIds: [rowId] } });
   }, [ann.annDispatch, dispatch]);

   const commitRowPages = useCallback(async (
      rowId: string,
      pdfCache: Map<string, Promise<Blob | null>>,
   ): Promise<boolean> => {
      if (!requireSession(session)) {
         return false;
      }
      let row = getState().rows[rowId];
      if (!row || row.job !== "idle" || !selectPageApprovalPlan(row).canApprove) {
         return false;
      }
      dispatch({ type: ServitutReportActionType.CommitStarted, payload: { rowIds: [rowId] } });
      try {
         const listed = await listAnnotationsForRow(row, session.organizationId);
         row = getState().rows[rowId] || row;
         const existing = findAllServitutDocumentAnnotations(listed, row.identity.datoloebenr);
         const overwrite = existing[0];
         let file = {
            s3Key: overwrite?.document?.s3Key || (row.document.kind === "annotation" ? row.document.s3Key : "") || (row.document.kind === "upload" ? row.document.s3Key : ""),
            filename: documentFilename(row) || overwrite?.document?.filename,
            s3Url: overwrite?.document?.s3Url || (row.document.kind === "annotation" ? row.document.s3Url : undefined),
            mimeType: overwrite?.document?.mimeType || (row.document.kind === "annotation" ? row.document.mimeType : undefined),
            size: overwrite?.document?.size || (row.document.kind === "annotation" ? row.document.size : undefined),
            existing: overwrite,
         };
         if (!canCommitPagesWithoutPdfFetch(row, overwrite) || !file.s3Key) {
            const sourcePdf = await getCachedRowPdfBlob(row, pdfCache);
            if (!sourcePdf) {
               throw new Error(Localization.getText("No PDF available for this document"));
            }
            row = getState().rows[rowId] || row;
            const pagesToKeep = normalizePages(row.pages.selected.length ? row.pages.selected : row.pages.aiSuggested);
            const pageCount = await getPdfPageCount(sourcePdf).catch(() => 0);
            const pagesToCut = pageCount ? pagesToKeep.filter((page) => page >= 1 && page <= pageCount) : pagesToKeep;
            const documentBlob = row.landRegistry.handling === "electronicZipPackage"
               ? sourcePdf
               : pagesToCut.length
                  ? await cutPdfToSelectedPages(sourcePdf, pagesToCut)
                  : sourcePdf;
            const filename = documentFilename(row);
            const uploaded = await uploadOverlayImage(new File([documentBlob], filename, { type: "application/pdf" }));
            file = {
               s3Key: uploaded.key,
               filename,
               s3Url: uploaded.url,
               mimeType: "application/pdf",
               size: documentBlob.size,
               existing: overwrite,
            };
         }
         row = getState().rows[rowId] || row;
         const record = await ann.saveAnnotation(buildPageDocumentSaveInput(row, {
            organizationId: session.organizationId,
            userId: session.userId,
            userName: session.userName,
         }, file));
         for (const ignoreNote of findIgnoreNotes(listed, row.identity.datoloebenr)) {
            if (ignoreNote.id) {
               try {
                  await ann.deleteAnnotation(ignoreNote.id, session.organizationId);
               } catch (error) {
                  console.warn("[Servitutredegørelse] failed to delete ignore note", rowId, ignoreNote.id, error);
               }
            }
         }
         for (const duplicate of existing.slice(1)) {
            if (duplicate.id && duplicate.id !== overwrite?.id) {
               try {
                  await ann.deleteAnnotation(duplicate.id, session.organizationId);
               } catch (error) {
                  console.warn("[Servitutredegørelse] Failed to delete duplicate servitut text annotation", error);
               }
            }
         }
         finishRowCommit(rowId, [record]);
         return true;
      } catch (error) {
         console.error("[Servitutredegørelse] failed to save annotation", rowId, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
         dispatch({ type: ServitutReportActionType.CommitFailed, payload: { rowIds: [rowId] } });
         return false;
      }
   }, [ann, dispatch, finishRowCommit, getState, session]);

   const persistRowIgnore = useCallback(async (rowId: string): Promise<boolean> => {
      if (!requireSession(session)) {
         return false;
      }
      const row = getState().rows[rowId];
      if (!row || !selectIsRejected(row)) {
         return false;
      }
      dispatch({ type: ServitutReportActionType.CommitStarted, payload: { rowIds: [rowId] } });
      try {
         const listed = await listAnnotationsForRow(row, session.organizationId);
         const existingDocs = findAllServitutDocumentAnnotations(listed, row.identity.datoloebenr);
         const overwrite = existingDocs[0];
         const records: ANNAnnotationRecord[] = [];
         if (overwrite?.document?.s3Key) {
            records.push(await ann.saveAnnotation(buildPageDocumentSaveInput(row, {
               organizationId: session.organizationId,
               userId: session.userId,
               userName: session.userName,
            }, {
               s3Key: overwrite.document.s3Key,
               filename: overwrite.document.filename,
               s3Url: overwrite.document.s3Url,
               mimeType: overwrite.document.mimeType,
               size: overwrite.document.size,
               existing: overwrite,
            })));
         }
         const ignoreNotes = findIgnoreNotes(listed, row.identity.datoloebenr);
         const note = await ann.saveAnnotation(buildIgnoreNoteSaveInput(row, {
            organizationId: session.organizationId,
            userId: session.userId,
            userName: session.userName,
         }, ignoreNotes[0]));
         records.push(note);
         for (const extra of ignoreNotes.slice(1)) {
            if (extra.id && extra.id !== note.id) {
               try {
                  await ann.deleteAnnotation(extra.id, session.organizationId);
               } catch (error) {
                  console.warn("[Servitutredegørelse] failed to delete duplicate ignore note", rowId, extra.id, error);
               }
            }
         }
         finishRowCommit(rowId, records);
         return true;
      } catch (error) {
         console.error("[Servitutredegørelse] failed to ignore easement", rowId, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
         dispatch({ type: ServitutReportActionType.CommitFailed, payload: { rowIds: [rowId] } });
         return false;
      }
   }, [ann, dispatch, finishRowCommit, getState, session]);

   const approvePages = useCallback(async (rowIds: string[]) => {
      const eligible = rowIds.filter((id) => {
         const row = getState().rows[id];
         return row && !selectIsApproved(row) && selectPageApprovalPlan(row).canApprove && row.job === "idle";
      });
      if (!eligible.length) {
         return;
      }
      const pdfCache = new Map<string, Promise<Blob | null>>();
      await runWithConcurrency(eligible, SERVITUT_PDF_SAVE_CONCURRENCY, async (rowId) => {
         const row = getState().rows[rowId];
         if (!row || selectIsApproved(row) || row.job !== "idle" || !selectPageApprovalPlan(row).canApprove) {
            return;
         }
         const origin = selectPageApprovalPlan(row).differs || !row.pages.aiSuggested.length ? "person" : "ai";
         const differs = selectPageApprovalPlan(row).differs;
         dispatch({ type: ServitutReportActionType.ApprovePages, payload: { rowId, origin } });
         await commitRowPages(rowId, pdfCache);
         if (differs && getState().rows[rowId]?.fields.resume.acceptedNoteId) {
            message.info(Localization.getText("Derived analysis annotations were marked outdated"));
         }
      });
   }, [commitRowPages, dispatch, getState]);

   const rejectPages = useCallback(async (rowIds: string[]) => {
      const eligible = rowIds.filter((id) => {
         const row = getState().rows[id];
         return row && !selectIsRejected(row) && (row.job === "idle" || row.job === "saving");
      });
      if (!eligible.length) {
         return;
      }
      await runWithConcurrency(eligible, SERVITUT_NOTE_SAVE_CONCURRENCY, async (rowId) => {
         const row = getState().rows[rowId];
         if (!row || selectIsRejected(row) || (row.job !== "idle" && row.job !== "saving")) {
            return;
         }
         dispatch({ type: ServitutReportActionType.RejectPages, payload: { rowId, origin: "person" } });
         await persistRowIgnore(rowId);
      });
   }, [dispatch, getState, persistRowIgnore]);

   const revertPages = useCallback(async (rowIds: string[], options?: { quiet?: boolean }) => {
      if (!requireSession(session)) {
         return;
      }
      const eligible = rowIds.filter((id) => {
         const row = getState().rows[id];
         return row && (selectIsApproved(row) || selectIsRejected(row)) && (row.job === "idle" || row.job === "saving");
      });
      if (!eligible.length) {
         return;
      }
      let undone = 0;
      await runWithConcurrency(eligible, SERVITUT_NOTE_SAVE_CONCURRENCY, async (rowId) => {
         const row = getState().rows[rowId];
         if (!row || !(selectIsApproved(row) || selectIsRejected(row)) || (row.job !== "idle" && row.job !== "saving")) {
            return;
         }
         const ignored = selectIsRejected(row);
         dispatch({ type: ServitutReportActionType.CommitStarted, payload: { rowIds: [rowId] } });
         dispatch({ type: ServitutReportActionType.RevertPages, payload: { rowId } });
         try {
            const items = await listAnnotationsForRow(row, session.organizationId);
            if (ignored) {
               for (const item of findIgnoreNotes(items, row.identity.datoloebenr)) {
                  if (item.id) {
                     try {
                        await ann.deleteAnnotation(item.id, session.organizationId);
                     } catch (error) {
                        console.warn("[Servitutredegørelse] failed to delete ignore note", rowId, item.id, error);
                     }
                  }
               }
               const overwrite = findAllServitutDocumentAnnotations(items, row.identity.datoloebenr)[0];
               if (overwrite?.document?.s3Key && overwrite.document.provenance?.reviewState === "rejected") {
                  await ann.saveAnnotation(buildPageDocumentSaveInput(row, {
                     organizationId: session.organizationId,
                     userId: session.userId,
                     userName: session.userName,
                  }, {
                     s3Key: overwrite.document.s3Key,
                     filename: overwrite.document.filename,
                     s3Url: overwrite.document.s3Url,
                     mimeType: overwrite.document.mimeType,
                     size: overwrite.document.size,
                     existing: overwrite,
                  }));
               }
               const afterIgnore = getState().rows[rowId];
               if (afterIgnore && needsDeferredAktFetch(afterIgnore)) {
                  await fetchAndBindLandRegistry(dispatch, getState, rowId);
               }
               undone += 1;
               return;
            }
            for (const item of items) {
               if (!item.id) {
                  continue;
               }
               if (!isServitutAnalysisFieldNote(item) && !isServitutPdfDocumentAnnotation(item) && !isServitutIgnoreNote(item)) {
                  continue;
               }
               try {
                  await ann.deleteAnnotation(item.id, session.organizationId);
               } catch (error) {
                  console.warn("[Servitutredegørelse] failed to delete servitut annotation", rowId, item.id, error);
               }
            }
            const reverted = getState().rows[rowId];
            if (reverted && (needsLandRegistryRebind(reverted) || needsDeferredAktFetch(reverted))) {
               await fetchAndBindLandRegistry(dispatch, getState, rowId);
            }
            undone += 1;
         } catch (error) {
            console.error("[Servitutredegørelse] failed to revert page approval", rowId, error);
            if (!options?.quiet) {
               message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
            }
         } finally {
            dispatch({ type: ServitutReportActionType.CommitFailed, payload: { rowIds: [rowId] } });
         }
      });
      if (!options?.quiet && undone > 1) {
         message.info(Localization.getFormattedText("Undid {count} page approvals", { count: undone }));
      }
   }, [ann, dispatch, getState, session]);

   const acceptField = useCallback(async (rowId: string, field: ServitutAnalysisFieldKey, text?: string, quiet?: boolean) => {
      if (!requireSession(session)) {
         return;
      }
      const row = getState().rows[rowId];
      if (!row) {
         return;
      }
      if (row.job !== "idle") {
         if (!quiet) {
            message.info(Localization.getText("Cannot save while AI is running"));
         }
         return;
      }
      const value = (text ?? selectDisplayedFieldText(getState(), row, field)).trim();
      if (!value) {
         if (!quiet) {
            message.error(Localization.getText("No value to accept"));
         }
         return;
      }
      const lock = `${rowId}:${field}`;
      if (fieldSaveRef.current.has(lock)) {
         return;
      }
      fieldSaveRef.current.add(lock);
      dispatch({ type: ServitutReportActionType.CommitStarted, payload: { rowIds: [rowId] } });
      try {
         const listed = await listAnnotationsForRow(row, session.organizationId);
         const siblings = listFieldNotesOfType(listed, field, row.identity.datoloebenr);
         const existing = siblings.find((note) => note.verified) || siblings[0];
         const record = await commitFieldNote({
            row,
            field,
            text: value,
            session: {
               organizationId: session.organizationId,
               userId: session.userId,
               userName: session.userName,
            },
            existing,
            siblings,
            saveAnnotation: ann.saveAnnotation,
            deleteAnnotation: ann.deleteAnnotation,
         });
         dispatch({
            type: ServitutReportActionType.AcceptField,
            payload: { rowId, field, noteId: record.id, text: value },
         });
         upsertAnnRecords(ann.annDispatch, [record]);
         if (!quiet) {
            message.info(Localization.getText("Field annotation saved"));
         }
      } catch (error) {
         console.error("[Servitutredegørelse] failed to save field annotation", rowId, field, error);
         if (!quiet) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
         }
      } finally {
         fieldSaveRef.current.delete(lock);
         dispatch({ type: ServitutReportActionType.CommitFailed, payload: { rowIds: [rowId] } });
      }
   }, [ann, dispatch, getState, session]);

   const revertField = useCallback(async (rowId: string, field: ServitutAnalysisFieldKey, quiet?: boolean) => {
      const row = getState().rows[rowId];
      if (!row?.fields[field].acceptedNoteId) {
         return;
      }
      if (row.job !== "idle") {
         if (!quiet) {
            message.info(Localization.getText("Cannot save while AI is running"));
         }
         return;
      }
      dispatch({ type: ServitutReportActionType.CommitStarted, payload: { rowIds: [rowId] } });
      try {
         if (session.organizationId && row.fields[field].acceptedNoteId) {
            await ann.deleteAnnotation(row.fields[field].acceptedNoteId, session.organizationId);
         }
         dispatch({ type: ServitutReportActionType.RevertField, payload: { rowId, field } });
      } catch (error) {
         console.error("[Servitutredegørelse] failed to undo field annotation", rowId, field, error);
         if (!quiet) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
         }
      } finally {
         dispatch({ type: ServitutReportActionType.CommitFailed, payload: { rowIds: [rowId] } });
      }
   }, [ann, dispatch, getState, session.organizationId]);

   const uploadPdf = useCallback(async (rowId: string, file: File) => {
      if (!requireSession(session)) {
         return;
      }
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
         message.error(Localization.getText("Please upload a PDF file"));
         return;
      }
      if (!(await pdfHasExtractableText(file))) {
         message.error(Localization.getText("The uploaded PDF has no extractable text"));
         return;
      }
      const row = getState().rows[rowId];
      if (!row || row.job !== "idle") {
         return;
      }
      try {
         const uploaded = await uploadOverlayImage(file);
         const metadata: LLMMetadata = {
            id: rowId,
            document: file.name,
            documentUrl: null,
            aktId: rowId,
            tekster: [row.landRegistry.summary].filter(Boolean),
            fromOvrigDataSource: false,
         };
         if (row.fields.resume.acceptedNoteId || row.document.kind === "annotation" || selectIsRejected(row)) {
            const listed = await listAnnotationsForRow(row, session.organizationId);
            for (const note of listed) {
               if (note.id && (isServitutAnalysisFieldNote(note) || isServitutIgnoreNote(note))) {
                  try {
                     await ann.deleteAnnotation(note.id, session.organizationId);
                  } catch (error) {
                     console.warn("[Servitutredegørelse] failed to delete field annotation", rowId, note.id, error);
                  }
               }
            }
         }
         dispatch({
            type: ServitutReportActionType.UploadPdf,
            payload: { rowId, s3Key: uploaded.key, filename: file.name, selectedPages: [], metadata },
         });
         message.info(Localization.getText("Using uploaded PDF"));
      } catch (error) {
         console.error("[Servitutredegørelse] custom PDF upload failed", rowId, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
      }
   }, [ann, dispatch, getState, session]);

   const selectAkt = useCallback(async (rowId: string, aktId: string, source: PreparedAktSource = "customAkt") => {
      if (!requireSession(session)) {
         return;
      }
      const parsed = parseTingbogAktIdInput(aktId);
      if (!parsed) {
         message.error(Localization.getText("Enter an akt id, for example 51_M_205"));
         return;
      }
      const row = getState().rows[rowId];
      if (!row || row.job !== "idle") {
         return;
      }
      if (isDigitalRow(row) && !isUserFileDocument(row)) {
         return;
      }
      if (row.document.kind === "tingbog_akt" && row.document.aktId === parsed && row.document.source === source) {
         return;
      }
      dispatch({ type: ServitutReportActionType.FetchAktStarted, payload: { rowIds: [rowId] } });
      try {
         const hint = row.landRegistry.includedBfes
            .map((bfe) => getState().aktSearchByBfe[String(bfe)])
            .find(Boolean);
         const bound = await bindRowToAkt(row, {
            aktId: parsed,
            source,
            stamOplysninger: hint,
         });
         const listed = await listAnnotationsForRow(row, session.organizationId);
         for (const item of listed) {
            if (!item.id) {
               continue;
            }
            if (!isServitutAnalysisFieldNote(item) && !isServitutPdfDocumentAnnotation(item) && !isServitutIgnoreNote(item)) {
               continue;
            }
            try {
               await ann.deleteAnnotation(item.id, session.organizationId);
            } catch (error) {
               console.warn("[Servitutredegørelse] failed to delete stale servitut annotation", rowId, item.id, error);
            }
         }
         dispatch({
            type: ServitutReportActionType.SelectAkt,
            payload: {
               rowId,
               document: bound.row.document,
               pdfStatus: bound.row.pdfStatus,
               sourceMessage: bound.row.sourceMessage,
               metadata: bound.metadata,
            },
         });
         message.info(Localization.getFormattedText("Using akt {aktId}", { aktId: parsed }));
      } catch (error) {
         console.error("[Servitutredegørelse] failed to select akt", rowId, parsed, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to fetch akt"));
         dispatch({ type: ServitutReportActionType.FetchAktFailed, payload: { rowIds: [rowId] } });
      }
   }, [ann, dispatch, getState, session]);

   const resolvePaatale = useCallback(async (rowId: string) => {
      if (!requireSession(session)) {
         return;
      }
      const row = getState().rows[rowId];
      if (!row || !selectCanResolvePaatale(row)) {
         message.error(Localization.getText("No historical entitled party to resolve"));
         return;
      }
      const historicalName = selectHistoricalPaataleName(row);
      dispatch({ type: ServitutReportActionType.SetResolvingPaatale, payload: { rowId, resolving: true } });
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: session.userId,
            userName: session.userName,
            organizationId: session.organizationId,
         });
         let metadata: LLMMetadata = {
            ...buildRowMetadata(row, getState().metadataById[rowId], getState().caseDescription),
            skipPaataleberettigetResolution: false,
         };
         if (isDigitalRow(row) && !isUserFileDocument(row) && !looksLikeXml(metadata.document)) {
            const fresh = await fetchDigitalXmlMetadata(row);
            if (fresh) {
               metadata = { ...fresh, caseDescription: metadata.caseDescription, skipPaataleberettigetResolution: false };
            }
         }
         let resultItem: LLMAnalysisResultItem | undefined;
         const mode = selectContentAnalysisMode(row, metadata);
         if (mode === "xml") {
            const result = await analyzer.analyzePdfUrlBatch([metadata]);
            resultItem = result.items?.find((entry) => entry.id === rowId) || result.items?.[0];
         } else {
            const pdfBlob = await getCutPdfForContent(row) || await getRowSourcePdfBlob(row);
            if (pdfBlob) {
               resultItem = await analyzer.evaluateServitutDocument({
                  metadata,
                  pdfBlob,
                  documentFileName: documentFilename(row),
               });
            } else if (metadata.documentUrl) {
               const result = await analyzer.analyzePdfUrlBatch([metadata]);
               resultItem = result.items?.[0];
            }
         }
         const resolution = resultItem?.paataleberettiget_resolution || failedPaatale(historicalName, "not_found");
         const previous = row.analysis?.item;
         const merged: LLMAnalysisResultItem = {
            processing_method: previous?.processing_method || resultItem?.processing_method || "llm",
            reasoning: previous?.reasoning || resultItem?.reasoning || "",
            original_pages: previous?.original_pages ?? resultItem?.original_pages ?? 0,
            extracted_pages: previous?.extracted_pages ?? resultItem?.extracted_pages ?? 0,
            ...previous,
            ...resultItem,
            id: rowId,
            aktId: previous?.aktId ?? resultItem?.aktId ?? null,
            pages_kept: row.pages.selected.length ? row.pages.selected : (previous?.pages_kept || resultItem?.pages_kept || []),
            paataleberettiget: previous?.paataleberettiget || resultItem?.paataleberettiget || historicalName,
            paataleberettiget_resolution: resolution,
         };
         dispatch({
            type: ServitutReportActionType.PaataleResolved,
            payload: { rowId, item: merged },
         });
         message.info(
            resolution.suggested_current_name
               ? Localization.getText("Current entitled party suggestion ready.")
               : Localization.getText("Current entitled party AI could not find"),
         );
      } catch (error) {
         console.error("[Servitutredegørelse] påtaleberettiget resolution failed", rowId, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to resolve current entitled party"));
      } finally {
         dispatch({ type: ServitutReportActionType.SetResolvingPaatale, payload: { rowId, resolving: false } });
      }
   }, [dispatch, getState, session]);

   const exportTreated = useCallback(async (mode: "excel" | "zip") => {
      const state = getState();
      const rows = selectOrderedRows(state);
      if (!rows.length) {
         message.error(Localization.getText("There are no easements to export."));
         return;
      }
      dispatch({
         type: ServitutReportActionType.SetExporting,
         payload: {
            exporting: true,
            progress: { completedSteps: 0, totalSteps: mode === "excel" ? 1 : 2 + rows.length, stage: "spreadsheet", activeRowKey: null },
         },
      });
      try {
         if (mode === "excel") {
            const blob = buildServitutExcelBlob(rows, state.columns, state.columnLabels, {
               includeAnalysisFields: state.phase === "B",
               caseDescription: state.caseDescription,
            });
            ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.xlsx`, blob);
         } else {
            const blob = await buildServitutZip(rows, state.columns, state.columnLabels, async (progress) => {
               dispatch({ type: ServitutReportActionType.SetExporting, payload: { exporting: true, progress } });
            }, {
               includeAnalysisFields: state.phase === "B",
               caseDescription: state.caseDescription,
            });
            ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.zip`, blob);
         }
         message.info(Localization.getFormattedText("{count} easements have been exported.", { count: rows.length }));
      } catch (error) {
         console.error("[Servitutredegørelse] export failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Export failed"));
      } finally {
         dispatch({ type: ServitutReportActionType.SetExporting, payload: { exporting: false, progress: null } });
      }
   }, [dispatch, getState]);

   const acceptAllField = useCallback(async (entries: Array<{ rowId: string; text?: string }>, field: ServitutAnalysisFieldKey) => {
      const pending = entries.filter((entry) => getState().rows[entry.rowId]?.job === "idle");
      await runWithConcurrency(pending, SERVITUT_NOTE_SAVE_CONCURRENCY, async (entry) => {
         await acceptField(entry.rowId, field, entry.text, true);
      });
      if (pending.length) {
         message.info(Localization.getFormattedText("Accepted {count} {field} annotations", {
            count: pending.length,
            field: analysisFieldLabel(field),
         }));
      }
   }, [acceptField, getState]);

   const revertAllField = useCallback(async (rowIds: string[], field: ServitutAnalysisFieldKey) => {
      const pending = rowIds.filter((id) => getState().rows[id]?.fields[field].acceptedNoteId && getState().rows[id]?.job === "idle");
      await runWithConcurrency(pending, SERVITUT_NOTE_SAVE_CONCURRENCY, async (rowId) => {
         await revertField(rowId, field, true);
      });
      if (pending.length) {
         message.info(Localization.getFormattedText("Undid {count} {field} annotations", {
            count: pending.length,
            field: analysisFieldLabel(field),
         }));
      }
   }, [getState, revertField]);

   return {
      aiProgress,
      prepare,
      hydrateFromAnn,
      locateRows,
      analyzeContentRows,
      stopAnalysis: (phase: "A" | "B") => abortJobs(phase === "B" ? "content" : "locate"),
      stopAktFetch: () => {
         abortJobs("akt");
         const rowIds = getState().aktFetchIds;
         if (rowIds.length) {
            dispatch({ type: ServitutReportActionType.AktFetchBatchStopped, payload: { rowIds } });
         }
      },
      approvePages,
      rejectPages,
      revertPages,
      acceptField,
      revertField,
      acceptAllField,
      revertAllField,
      uploadPdf,
      selectAkt,
      resolvePaatale,
      exportTreated,
      publishAnn,
      hasLocatableRows: () => selectHasLocatableRows(getState()),
      hasContentRows: () => selectHasContentAnalyzableRows(getState()),
      mapAktPagesToCutSelection,
      canonicalDocumentId,
   };
}
